import { useRef, useState } from "react";
import {
	ThemeProvider,
	createTheme,
	CssBaseline,
	Typography,
	Box,
	Button,
	Stack,
	Snackbar,
	Alert,
	IconButton,
	Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PropertiesTable from "./components/PropertiesTable";
import LoginForm from "./components/LoginForm";
import ChangePasswordForm from "./components/ChangePasswordForm";
import LogoutIcon from "@mui/icons-material/Logout";
import LockIcon from "@mui/icons-material/Lock";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ParkIcon from "@mui/icons-material/Park";
import "./datagrid-overrides.css";
import axios from "axios";

const VERSION = "1.0.4";
const theme = createTheme();

function App() {
	const tableRef = useRef();
	const [token, setToken] = useState(localStorage.getItem("token"));
	const [showChangePassword, setShowChangePassword] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const [menuAnchor, setMenuAnchor] = useState(null);

	const handleAdd = () => {
		if (tableRef.current && tableRef.current.openAddDialog) {
			tableRef.current.openAddDialog();
		}
	};

	const handleLogout = async () => {
		const URL = `${process.env.REACT_APP_BACKEND_URL}/api/users/logout`;
		const token = localStorage.getItem("token") || "";
		try {
			await axios.post(URL, {
				token,
			});
			localStorage.removeItem("token");
			setToken(null);
		} catch (err) {
			console.log(err.response?.data?.error || "Σφάλμα αποσύνδεσης. Προσπαθήστε ξανά.");
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<Box
				sx={{
					width: "100vw",
					minHeight: "100vh",
					p: 0,
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					minHeight: "100vh",
				}}
			>
				{/* Header always visible */}
				<Box
					component="header"
					sx={{
						bgcolor: "#003375",
						borderBottom: "1px solid #e0e0e0",
						px: 2,
						py: 4,
						display: "flex",
						alignItems: "center",
						position: "relative",
					}}
				>
					<img src="/images/govgr-logo.png" alt="govgr logo" style={{ height: 48, marginRight: 16 }} />
					<Box flex={1} />
					<Link href="/" underline="none" color="inherit" sx={{ display: "inline-block" }}>
						<Typography
							variant="h4"
							sx={{
								color: "#fff",
								fontWeight: 500,
								letterSpacing: 1,
								display: "flex",
								alignItems: "center",
								background: "#0f4f66",
								border: "2px solid #fff",
								borderRadius: "12px",
								px: 4,
								py: 1,
								boxShadow: 2,
								mr: 2,
								gap: 1,
							}}
						>
							<ParkIcon sx={{ color: "#fff", fontSize: 32, mr: 1 }} />
							Κατασκηνώσεις
						</Typography>
					</Link>
					{token && (
						<>
							<IconButton sx={{ color: "#fff" }} onClick={(e) => setMenuAnchor(e.currentTarget)}>
								<AccountCircle fontSize="large" />
							</IconButton>
							<Menu
								anchorEl={menuAnchor}
								open={Boolean(menuAnchor)}
								onClose={() => setMenuAnchor(null)}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
								transformOrigin={{ vertical: "top", horizontal: "right" }}
							>
								<MenuItem
									onClick={() => {
										setShowChangePassword((v) => !v);
										setMenuAnchor(null);
									}}
								>
									<LockIcon sx={{ mr: 1 }} /> Αλλαγή κωδικού
								</MenuItem>
								<MenuItem
									onClick={() => {
										handleLogout();
										setMenuAnchor(null);
									}}
								>
									<LogoutIcon sx={{ mr: 1 }} /> Αποσύνδεση
								</MenuItem>
							</Menu>
						</>
					)}
				</Box>

				{/* Main content area changes based on auth */}
				<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
					{!token ? (
						<Box
							sx={{
								flex: 1,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								minHeight: 0,
							}}
						>
							<LoginForm onLogin={setToken} />
						</Box>
					) : showChangePassword ? (
						<ChangePasswordForm
							onSuccess={() => {
								setSnackbar({ open: true, message: "Ο κωδικός άλλαξε επιτυχώς.", severity: "success" });
								setShowChangePassword(false);
							}}
						/>
					) : (
						<>
							<Stack direction="row" alignItems="center" spacing={2} sx={{ pl: 2, pt: 2, mb: 2 }}>
								<Button
									variant="contained"
									startIcon={<AddIcon />}
									onClick={handleAdd}
									sx={{ textTransform: "none", fontWeight: 500 }}
								>
									Προσθήκη εγγραφής
								</Button>
							</Stack>
							<PropertiesTable ref={tableRef} />
						</>
					)}
				</Box>

				{/* Footer always visible */}
				<Box
					component="footer"
					sx={{
						bgcolor: "#f5f5f5",
						color: "#333",
						py: 2,
						px: 2,
						textAlign: "center",
						fontSize: "0.95rem",
						borderTop: "1px solid #e0e0e0",
					}}
				>
					Άδεια χρήσης περιεχομένου: CC-BY-SA
					<br />
					Διεύθυνση Ηλεκτρονικής Διακυβέρνησης και Απλούστευσης Διαδικασιών του Υ.ΠΑΙ.Θ.Α. © 2025.
					<br />
					Επικοινωνία με την{" "}
					<a
						href="mailto:formes_support@minedu.gov.gr"
						style={{ color: "#003375", textDecoration: "underline", fontWeight: 500 }}
					>
						ΤΕΧΝΙΚΗ ΥΠΟΣΤΗΡΙΞΗ
					</a>{" "}
					&nbsp; &nbsp; Έκδοση Λογισμικού: {VERSION}
				</Box>
				<Snackbar
					open={snackbar.open}
					autoHideDuration={3000}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
				>
					<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
						{snackbar.message}
					</Alert>
				</Snackbar>
			</Box>
		</ThemeProvider>
	);
}

export default App;
